import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { ordersApi } from 'redux/api/ordersApi';
import { useAppDispatch, useAppSelector } from 'redux/hooks';

import { ordersDataService } from 'services';

import { useOrderModal } from 'hooks';

import { OrderModal } from 'components/OrderModal';

import * as S from './OrderButton.styles';

type OrderButtonProps = {
  subProductId: string;
  colorCode: string;
  disabled: boolean;
  itemCode?: string;
  mainFabricCode?: string;
};

export function OrderButton(props: OrderButtonProps): React.ReactElement {
  const { disabled, colorCode, itemCode, mainFabricCode } = props;
  const dispatch = useAppDispatch();
  const { orderId } = useAppSelector((state) => state.settings);
  const { t } = useTranslation();
  const [isInOrder, isInOrderSet] = useState<boolean>(false);
  const [isLoading, isLoadingSet] = useState<boolean>(false);
  const [orderItemId, orderItemIdSet] = useState<string>('');

  const getItemPayload = useCallback(
    () => ({
      productCode: itemCode ?? '',
      mainFabricCode: mainFabricCode ?? '',
      colorCode,
      sizes: [],
    }),
    [itemCode, mainFabricCode, colorCode]
  );

  const onSuccess = useCallback(() => {
    isInOrderSet(true);
  }, []);

  const { isOrderModalVisible, isOrderModalVisibleSet, isOrderLoading, onOrderSubmit } =
    useOrderModal({ getItemPayload, onSuccess });

  useEffect(() => {
    if (orderId && itemCode && colorCode) {
      ordersDataService
        .checkProductStatus(orderId, itemCode, colorCode, mainFabricCode)
        .then((res) => {
          const { data } = res;
          if (data.inOrder) {
            isInOrderSet(true);
            orderItemIdSet(data.itemId);
          } else {
            isInOrderSet(false);
            orderItemIdSet('');
          }
        })
        .catch(() => {
          isInOrderSet(false);
          orderItemIdSet('');
        });
    } else {
      isInOrderSet(false);
      orderItemIdSet('');
    }
  }, [orderId, itemCode, mainFabricCode, colorCode]);

  const addToOrder = () => {
    if (!orderId) {
      isOrderModalVisibleSet(true);
      return;
    }

    if (!isInOrder) {
      isLoadingSet(true);
      ordersDataService
        .addItem(orderId, {
          productCode: itemCode ?? '',
          mainFabricCode: mainFabricCode ?? '',
          colorCode,
          sizes: [],
        })
        .then((res) => {
          orderItemIdSet(res.data?.itemId ?? '');
          isInOrderSet(true);
          isLoadingSet(false);
          dispatch(ordersApi.util.invalidateTags([{ type: 'OrderHeader', id: orderId }]));
          toast.success(t('product.addedSuccessfully'));
        })
        .catch(() => {
          toast.error(t('product.errorAdding'));
          isLoadingSet(false);
        });
    } else {
      if (!orderItemId) {
        toast.error(t('product.errorRemoving'));
        return;
      }
      isLoadingSet(true);
      ordersDataService
        .deleteItem(orderId, orderItemId)
        .then(() => {
          isInOrderSet(false);
          orderItemIdSet('');
          isLoadingSet(false);
          dispatch(ordersApi.util.invalidateTags([{ type: 'OrderHeader', id: orderId }]));
          toast.success(t('product.removedSuccessfully'));
        })
        .catch(() => {
          toast.error(t('product.errorRemoving'));
          isLoadingSet(false);
        });
    }
  };

  return (
    <S.Container>
      <S.Button
        disabled={disabled}
        onClick={addToOrder}
        isLoading={isLoading}
        loadingColor="black"
        iconLeft={
          !disabled && isInOrder ? 'BsDashCircle' : disabled ? undefined : 'BsPlusCircle'
        }
      >
        {disabled
          ? t('product.unavailable')
          : isInOrder
          ? t('product.removeFromOrder')
          : t('product.addToOrder')}
      </S.Button>

      {isOrderModalVisible ? (
        <OrderModal
          onClose={() => isOrderModalVisibleSet(false)}
          onSubmit={onOrderSubmit}
          isLoading={isOrderLoading}
        />
      ) : undefined}
    </S.Container>
  );
}
